import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { SignOut } from "@phosphor-icons/react";

/**
 * Customer portal chrome — visually aligned with getfuelcard.com:
 *  - Manrope headings, Montserrat buttons, Inter body
 *  - Navy primary CTA, royal-blue accent
 *  - Pill nav links + pill sign-out button
 *  - Layered chip wordmark matching the site logo
 *
 * Phase-4 P2 (2026-09-11) — Customer invoice tab re-enabled. Nav
 * entry inserted between Transactions and Cards. Backend enforces
 * tenant isolation on GET /api/invoices*; write verbs remain
 * staff-only.
 */
const nav = [
  { to: "/portal", label: "Overview", end: true },
  { to: "/portal/price-lists", label: "Price Lists" },
  { to: "/portal/transactions", label: "Transactions" },
  { to: "/portal/invoices", label: "Invoices" },
  { to: "/portal/cards", label: "Cards" },
  { to: "/portal/change-requests", label: "Change Requests" },
  { to: "/portal/support", label: "Support" },
  { to: "/portal/user-access", label: "User Access" },
  { to: "/portal/security", label: "Security" },
  { to: "/portal/profile", label: "Profile" },
];

function BrandMark() {
  // Layered chip logomark — mirrors the site's stacked card icon
  // (cyan card on top, magenta card behind, navy card at the back).
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center mr-2"
      style={{ width: 32, height: 22, position: "relative" }}
    >
      <span
        style={{
          position: "absolute", inset: 0, borderRadius: 6,
          transform: "rotate(-8deg) translate(-4px, -1px)",
          background: "#202939",
        }}
      />
      <span
        style={{
          position: "absolute", inset: 0, borderRadius: 6,
          transform: "rotate(-2deg)",
          background: "#EE3C8B",
        }}
      />
      <span
        style={{
          position: "absolute", inset: 0, borderRadius: 6,
          transform: "rotate(6deg) translate(5px, 2px)",
          background: "linear-gradient(135deg,#5BBBFF 0%,#1A3FBA 100%)",
        }}
      />
    </span>
  );
}

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div
      className="portal-shell min-h-screen"
      data-testid="customer-layout"
    >
      <header className="portal-header">
        {/* R1 responsive width fix (2026-09-28): flex-wrap header, wider max, min-w-0 shrinkable children,
            responsive padding + gaps. Nav wraps to a second row on narrow desktops instead of forcing
            page-level horizontal overflow. No CSS zoom, no transform scale, no hidden nav. */}
        <div className="max-w-[1600px] mx-auto flex flex-wrap items-center justify-between gap-y-2 px-4 xl:px-6 py-3 min-h-16">
          <div className="flex flex-wrap items-center gap-x-4 xl:gap-x-6 gap-y-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate("/portal")}
              data-testid="portal-brand"
              className="flex items-center cursor-pointer no-brand shrink-0"
              style={{ background: "transparent", border: 0, padding: 0 }}
            >
              <BrandMark />
              <span className="portal-brand">
                get<span className="portal-brand-mark">Fuel</span>Card
              </span>
              <span className="portal-brand-label">PORTAL</span>
            </button>
            <nav className="flex flex-wrap items-center gap-1 min-w-0">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `portal-nav-link ${isActive ? "is-active" : ""}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3 min-w-0 shrink-0">
            <div className="text-right min-w-0">
              <div
                className="text-sm font-medium truncate"
                style={{ color: "#1F2937" }}
                data-testid="portal-user-name"
              >
                {user?.name || user?.customer_name || user?.email}
              </div>
              {/* Email row demoted to xl+ to reclaim ~180px of nav row width on 1280–1440 viewports.
                  Name + Sign out remain visible on every supported desktop width. */}
              <div className="hidden xl:block text-xs truncate" style={{ color: "#6B7280" }}>
                {user?.email}
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="portal-logout-button"
              className="gf-btn gf-btn-outline shrink-0"
            >
              <SignOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-4 xl:px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
